import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockApprovalService } from '@/services/mock/MockApprovalService';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { ApprovalDetailData, DelegationFormValues } from '@/types/domain/approval';
import { DocumentAttachment } from '@/types/domain';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { StatusBadge } from '@/components/data-display/StatusBadge';
import { animateFadeIn } from '@/lib/motion';
import { ROUTES } from '@/constants/routes';
import {
  ArrowLeft,
  Clock,
  FileText,
  Download,
  Building,
  User,
  Paperclip,
  CheckCircle2,
  ShieldCheck,
  Layers,
} from 'lucide-react';
import { ApprovalFinancialSummary } from '@/components/approvals/ApprovalFinancialSummary';
import { ApprovalWorkflowTimeline } from '@/components/approvals/ApprovalWorkflowTimeline';
import { ApprovalDecisionActionsBar } from '@/components/approvals/ApprovalDecisionActionsBar';
import {
  ApproveModal,
  RejectModal,
  SendBackModal,
  ClarificationModal,
  DelegateModal,
} from '@/components/approvals/ApprovalActionModals';

export const ApprovalDetailsPage: React.FC = () => {
  const { approvalId } = useParams<{ approvalId: string }>();
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<ApprovalDetailData | null>(null);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Decision Action Modal States
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isSendBackOpen, setIsSendBackOpen] = useState(false);
  const [isClarificationOpen, setIsClarificationOpen] = useState(false);
  const [isDelegateOpen, setIsDelegateOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const loadApprovalDetail = async () => {
    if (!approvalId) return;
    try {
      setIsLoading(true);
      const detail = await mockApprovalService.getApprovalDetailById(approvalId);
      if (!detail) {
        navigate(ROUTES.APPROVALS.LIST);
        return;
      }
      setData(detail);

      // Load attached verification documents (read-only)
      const docs = await mockDocumentCommentService.getDocuments('REQUEST', detail.relatedEntityId);
      setDocuments(docs);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    animateFadeIn(pageRef.current);
    loadApprovalDetail();
  }, [approvalId]);

  // Decision Handlers
  const handleApproveConfirm = async (comment?: string) => {
    if (!data) return;
    try {
      setIsProcessing(true);
      const isStep1Pending = !data.workflowChain || data.workflowChain.length === 0 || data.workflowChain[0]?.status !== 'Completed';
      if (isStep1Pending) {
        await mockApprovalService.approveStep(data.id, 1, comment || 'Departmental operational requirement signoff granted.', 'Department Head');
      } else {
        await mockApprovalService.approveStep(data.id, 2, comment || 'Cost center budget verified and financial clearance granted.', 'Finance Controller');
      }
      setIsApproveOpen(false);
      await loadApprovalDetail();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!data) return;
    try {
      setIsProcessing(true);
      await mockApprovalService.rejectRequest(data.id, reason);
      setIsRejectOpen(false);
      await loadApprovalDetail();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendBackConfirm = async (explanation: string) => {
    if (!data) return;
    try {
      setIsProcessing(true);
      await mockApprovalService.sendBackRequest(data.id, explanation);
      setIsSendBackOpen(false);
      await loadApprovalDetail();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClarificationConfirm = async (question: string) => {
    if (!data) return;
    try {
      setIsProcessing(true);
      await mockApprovalService.requestClarification(data.id, question);
      setIsClarificationOpen(false);
      await loadApprovalDetail();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelegateConfirm = async (payload: DelegationFormValues) => {
    if (!data) return;
    try {
      setIsProcessing(true);
      await mockApprovalService.delegateApproval(data.id, payload);
      setIsDelegateOpen(false);
      await loadApprovalDetail();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadAttachment = async (docId: string) => {
    const res = await mockDocumentCommentService.downloadDocument(docId);
    if (res?.downloadUrl) {
      window.open(res.downloadUrl, '_blank');
    }
  };

  if (isLoading || !data) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Authoritative Decision Workspace...
      </div>
    );
  }

  const isPending = data.status === 'Pending';
  const isOverdue = data.slaHoursRemaining < 0;
  const requisitionNumber = data.relatedEntityId || data.approvalNumber;

  const formatInr = (val: number) =>
    `₹${Number(val || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <div ref={pageRef} className="space-y-6 pb-28 font-poppins">
      {/* Top Header Navigation & Status Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.APPROVALS.LIST)}
            className="h-9 rounded-xl border-border text-foreground hover:bg-muted font-bold font-poppins cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Approvals Queue
          </Button>

          <div className="flex items-center gap-2">
            <span className="font-poppins text-xl font-extrabold text-foreground">{requisitionNumber}</span>
            <Badge variant="outline" className="text-[10px] font-bold uppercase">
              {data.capexOpex}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {/* SLA Countdown pill ONLY shown when request is pending */}
          {isPending && (
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border font-poppins ${
                isOverdue
                  ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20 animate-pulse'
                  : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20'
              }`}
            >
              <Clock className="h-3.5 w-3.5" />
              {isOverdue ? `SLA Overdue (${Math.abs(data.slaHoursRemaining)}h)` : `${data.slaHoursRemaining}h SLA Remaining`}
            </span>
          )}

          <StatusBadge
            status={data.status}
            category="PurchaseRequest"
            className="w-32 py-1 justify-center rounded-full font-poppins"
          />
        </div>
      </div>

      {/* Primary Executive Requisition Card */}
      <Card className="border-border rounded-2xl shadow-2xs font-poppins">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
            <div className="space-y-1.5 max-w-3xl">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-primary uppercase tracking-wider">
                  {data.approvalType.replace('_', ' ')}
                </span>
                <span className="text-xs text-foreground-muted">•</span>
                <span className="text-xs text-foreground-muted font-medium">
                  Task Reference: <span className="font-mono font-bold text-foreground">{data.approvalNumber}</span>
                </span>
              </div>
              <h1 className="font-poppins text-2xl font-extrabold text-foreground tracking-tight">{data.title}</h1>
              <p className="text-xs text-foreground-secondary font-medium">
                Submitted on {new Date(data.submittedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>

            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 text-right space-y-1 min-w-[220px]">
              <span className="text-[11px] font-bold text-foreground-muted uppercase tracking-wider">
                Total Authorization Value
              </span>
              <span className="font-poppins text-2xl font-black text-primary block">
                {formatInr(data.totalAmount)}
              </span>
            </div>
          </div>

          {/* Key Attributes Horizontal Metadata Strip */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-secondary">
            <div className="flex items-center gap-1.5">
              <span className="text-foreground-muted text-[11px] font-semibold uppercase">Requester:</span>
              <span className="font-bold text-foreground flex items-center gap-1">
                <User className="h-3.5 w-3.5 text-primary shrink-0" /> {data.requesterName}
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-foreground-muted text-[11px] font-semibold uppercase">Department:</span>
              <span className="font-bold text-foreground flex items-center gap-1">
                <Building className="h-3.5 w-3.5 text-primary shrink-0" /> {data.departmentName}
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-foreground-muted text-[11px] font-semibold uppercase">Cost Center & Plant:</span>
              <span className="font-bold text-foreground">{data.costCenter} • {data.plantName}</span>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-foreground-muted text-[11px] font-semibold uppercase">Priority:</span>
              <span className="font-bold text-amber-600 dark:text-amber-400 uppercase">{data.priority}</span>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-foreground-muted text-[11px] font-semibold uppercase">Required By:</span>
              <span className="font-mono font-bold text-foreground">{data.requiredByDate}</span>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-foreground-muted text-[11px] font-semibold uppercase">Current Stage:</span>
              <span className="font-bold text-primary">{data.currentStage}</span>
            </div>
          </div>

          {/* Integrated Business Justification (No separate bulky card) */}
          {data.businessJustification && (
            <div className="pt-2 border-t border-border/40">
              <span className="text-[11px] font-bold text-foreground-muted uppercase tracking-wider block mb-1">
                Business Justification:
              </span>
              <p className="text-xs text-foreground leading-relaxed bg-muted/30 p-3 rounded-xl border border-border/50 font-medium">
                "{data.businessJustification}"
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 1 Completed -> Awaiting Finance Budget Clearance Banner */}
      {data.status === 'Pending' && data.workflowChain?.[0]?.status === 'Completed' && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 font-poppins shadow-xs">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-500/20 text-amber-600 dark:text-amber-400 shrink-0">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="font-bold text-amber-800 dark:text-amber-300 text-sm">
                Manager Signoff Completed — Awaiting Finance Budget Clearance
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Departmental operational signoff granted by Marcus Vance. Finance Controller (Rachel Chen) must clear the Cost Center budget before SAP PR and RFQ sourcing can be generated.
              </p>
            </div>
          </div>
          <span className="px-3.5 py-1 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 font-bold text-xs border border-amber-500/30 w-fit shrink-0">
            Stage 2 of 2: Finance Clearance
          </span>
        </div>
      )}

      {/* Requisition Approved & Authorized Completion Banner */}
      {data.status === 'Approved' && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 font-poppins shadow-xs">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 shrink-0">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <p className="font-bold text-emerald-800 dark:text-emerald-300 text-sm">
                Requisition Authorized & Completed
              </p>
              <p className="text-xs text-emerald-700 dark:text-emerald-400">
                This requisition has received all required executive approvals and is released for Purchase Order generation.
              </p>
            </div>
          </div>
          <span className="px-3.5 py-1 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-bold text-xs border border-emerald-500/30 w-fit shrink-0">
            Fully Authorized
          </span>
        </div>
      )}

      {/* Full-Width Procurement Line Items Table */}
      <Card className="border-border rounded-2xl shadow-2xs font-poppins">
        <CardHeader className="pb-3 border-b border-border/60">
          <div className="flex items-center justify-between">
            <CardTitle className="font-poppins text-sm font-bold text-foreground flex items-center gap-2">
              <Layers className="h-4 w-4 text-primary" />
              Procurement Line Items ({data.items.length})
            </CardTitle>
            <span className="text-xs font-bold text-primary">
              Total Authorization: {formatInr(data.totalAmount)}
            </span>
          </div>
        </CardHeader>

        <CardContent className="pt-4 overflow-x-auto">
          <table className="w-full text-xs text-left font-poppins">
            <thead className="border-b border-border text-[11px] font-bold uppercase tracking-wider text-black dark:text-white">
              <tr>
                <th className="pb-2.5">Item Code</th>
                <th className="pb-2.5">Material Description</th>
                <th className="pb-2.5">Quantity / UOM</th>
                <th className="pb-2.5">Est. Unit Price</th>
                <th className="pb-2.5">Total Amount</th>
                <th className="pb-2.5">Preferred Vendor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {data.items.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                  <td className="py-3 font-mono font-bold text-primary">{item.itemCode}</td>
                  <td className="py-3 font-bold text-foreground">{item.description}</td>
                  <td className="py-3 font-semibold">{item.quantity} {item.uom}</td>
                  <td className="py-3 font-mono font-medium">{formatInr(item.unitPrice)}</td>
                  <td className="py-3 font-mono font-bold text-foreground">
                    {formatInr(item.totalAmount)}
                  </td>
                  <td className="py-3 font-medium text-foreground-secondary">{item.preferredVendor || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-border font-bold bg-muted/20">
              <tr>
                <td colSpan={4} className="py-3 text-foreground font-semibold">
                  Total Impact Across {data.items.length} Line Item{data.items.length > 1 ? 's' : ''}
                </td>
                <td className="py-3 font-mono text-sm text-primary font-extrabold">
                  {formatInr(data.totalAmount)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {/* Full-Width Horizontal Financial & Cost Center Budget Clearance Bar */}
      <ApprovalFinancialSummary
        estimatedCost={data.estimatedCost}
        budgetAvailable={data.budgetAvailable}
        budgetCommitted={data.budgetCommitted}
        budgetRemaining={data.budgetRemaining}
        budgetUtilizationPercentage={data.budgetUtilizationPercentage}
        budgetStatus={data.budgetStatus}
        currency={data.currency}
        costCenter={data.costCenter}
        companyCode={data.companyCode}
      />

      {/* Balanced 2-Column Lower Section: Supporting Documents (Left) & Workflow Timeline (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 font-poppins">
        {/* Left: Attached Supporting Documents & Quotations Verification Card */}
        <Card className="border-border rounded-2xl shadow-2xs font-poppins h-fit">
          <CardHeader className="pb-3 border-b border-border/60">
            <div className="flex items-center justify-between">
              <CardTitle className="font-poppins text-sm font-bold text-foreground flex items-center gap-2">
                <Paperclip className="h-4 w-4 text-primary" />
                Supporting Documents & Quotations ({documents.length})
              </CardTitle>
              <span className="text-[11px] text-foreground-muted font-medium">
                Audit attachments
              </span>
            </div>
          </CardHeader>

          <CardContent className="pt-4">
            {documents.length === 0 ? (
              <div className="text-center py-8 text-xs text-foreground-muted">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
                No external supporting documents attached to this requisition.
              </div>
            ) : (
              <div className="space-y-2.5">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-border bg-card/60 hover:bg-card hover:border-primary/40 transition-all text-xs"
                  >
                    <div className="flex items-center gap-2.5 min-w-0 pr-2">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                        <FileText className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-foreground truncate" title={doc.fileName}>
                          {doc.fileName}
                        </p>
                        <div className="flex items-center gap-2 text-[10px] text-foreground-muted">
                          <span className="font-mono">{(doc.fileSize / 1024).toFixed(1)} KB</span>
                          <span>•</span>
                          <Badge variant="outline" className="text-[9px] py-0 px-1 font-semibold">
                            {doc.category}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadAttachment(doc.id)}
                      className="h-8 px-2.5 text-xs text-primary hover:bg-primary/10 rounded-lg cursor-pointer shrink-0"
                      title="Download / View Document"
                    >
                      <Download className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: Sequential Approval Workflow Matrix */}
        <div className="h-fit">
          <ApprovalWorkflowTimeline
            workflow={data.workflowChain}
            currentStage={data.currentStage}
          />
        </div>
      </div>

      {/* Fixed Authoritative Decision Toolbar at Bottom (Only when Pending) */}
      {isPending && (
        <ApprovalDecisionActionsBar
          onApprove={() => setIsApproveOpen(true)}
          onReject={() => setIsRejectOpen(true)}
          onSendBack={() => setIsSendBackOpen(true)}
          onRequestClarification={() => setIsClarificationOpen(true)}
          onDelegate={() => setIsDelegateOpen(true)}
          canApprove={data.canCurrentUserApprove}
          canDelegate={data.canCurrentUserDelegate}
          isProcessing={isProcessing}
          approveLabel={
            data.workflowChain?.[0]?.status === 'Completed'
              ? 'Clear Budget & Generate SAP PR'
              : 'Authorize Manager Signoff'
          }
        />
      )}

      {/* Decision Action Modals */}
      <ApproveModal
        isOpen={isApproveOpen}
        onClose={() => setIsApproveOpen(false)}
        onConfirm={handleApproveConfirm}
        reqNumber={requisitionNumber}
        title={data.title}
        totalAmount={data.totalAmount}
        currency={data.currency}
        isLoading={isProcessing}
      />

      <RejectModal
        isOpen={isRejectOpen}
        onClose={() => setIsRejectOpen(false)}
        onConfirm={handleRejectConfirm}
        reqNumber={requisitionNumber}
        isLoading={isProcessing}
      />

      <SendBackModal
        isOpen={isSendBackOpen}
        onClose={() => setIsSendBackOpen(false)}
        onConfirm={handleSendBackConfirm}
        reqNumber={requisitionNumber}
        isLoading={isProcessing}
      />

      <ClarificationModal
        isOpen={isClarificationOpen}
        onClose={() => setIsClarificationOpen(false)}
        onConfirm={handleClarificationConfirm}
        reqNumber={requisitionNumber}
        isLoading={isProcessing}
      />

      <DelegateModal
        isOpen={isDelegateOpen}
        onClose={() => setIsDelegateOpen(false)}
        onConfirm={handleDelegateConfirm}
        reqNumber={requisitionNumber}
        isLoading={isProcessing}
      />
    </div>
  );
};
